// SOK-04 (puntos 4 y 5): cobros que el tótem no alcanzó a confirmar.
//
// Un cobro POSAPI se anota en localStorage al empezar y se borra cuando se sabe cómo terminó
// (pagado o cancelado). Si la red se cae, si se acaba la espera o si la app se recarga a medio
// cobro, el registro queda y se vuelve a consultar al volver la red. Así ningún pago aprobado en el
// POS queda sin que el backend lo registre.
//
// Volver a consultar nunca cobra: es checkCheckoutStatus del mismo carro o una lectura de Firestore,
// nunca un getCheckout nuevo (BE-04, kiosk-backend#52, deja el carro en at_terminal mientras el
// proveedor no da una respuesta final).

export const PENDING_PAYMENTS_KEY = 'kiosk_pending_payment';

// Pasado este tiempo el registro se descarta: la conciliación con el proveedor ya no es del tótem (BE-05).
export const PENDING_PAYMENT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

// Tope para que un tótem que pierde la red seguido no llene el localStorage.
export const PENDING_PAYMENTS_MAX = 20;

// De dónde salió cart_id: de la respuesta de getCheckout (se puede consultar checkCheckoutStatus)
// o de Firestore, cuando getCheckout no respondió (solo se lee el carro, como en KIO-03).
export const CART_ID_SOURCE = Object.freeze({ CHECKOUT: 'checkout', FIRESTORE: 'firestore' });

const getStorage = () => {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
};

const isRecord = (value) => value && typeof value === 'object' && typeof value.id === 'string' && typeof value.started_at === 'string';

/**
 * Registros guardados. Un valor dañado o de otra versión se ignora en vez de romper el cobro.
 *
 * @param {Storage | null} [storage]
 * @returns {Array<{id: string, started_at: string, method: string | null, cart_id: string | null, cart_id_source: string | null, previous_cart_id: string | null}>}
 */
export function readPendingPayments(storage = getStorage()) {
  if (!storage) return [];
  try {
    const parsed = JSON.parse(storage.getItem(PENDING_PAYMENTS_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter(isRecord) : [];
  } catch {
    return [];
  }
}

function writePendingPayments(records, storage) {
  if (!storage) return;
  try {
    if (records.length === 0) storage.removeItem(PENDING_PAYMENTS_KEY);
    else storage.setItem(PENDING_PAYMENTS_KEY, JSON.stringify(records.slice(-PENDING_PAYMENTS_MAX)));
  } catch (error) {
    console.warn('No se pudo guardar el cobro pendiente:', error);
  }
}

/**
 * Anota un cobro que empieza.
 *
 * @param {object} params
 * @param {Date} params.startedAt
 * @param {string | null} params.method - `type_details` del método (Getnet, MercadoPago…).
 * @param {string | null} params.previousCartId - Carro anterior del tótem, para no confundirlo al buscar en Firestore.
 * @returns {object} El registro guardado.
 */
export function addPendingPayment({ startedAt, method = null, previousCartId = null }, storage = getStorage()) {
  const record = {
    id: `${startedAt.getTime()}`,
    started_at: startedAt.toISOString(),
    method,
    cart_id: null,
    cart_id_source: null,
    previous_cart_id: previousCartId,
  };
  writePendingPayments([...readPendingPayments(storage).filter((r) => r.id !== record.id), record], storage);
  return record;
}

/** Agrega datos a un registro (p. ej. el cart_id cuando se conoce). Si ya no está, no hace nada. */
export function updatePendingPayment(id, patch, storage = getStorage()) {
  const records = readPendingPayments(storage);
  if (!records.some((r) => r.id === id)) return null;
  const next = records.map((r) => (r.id === id ? { ...r, ...patch } : r));
  writePendingPayments(next, storage);
  return next.find((r) => r.id === id);
}

export function getPendingPayment(id, storage = getStorage()) {
  return readPendingPayments(storage).find((r) => r.id === id) || null;
}

export function removePendingPayment(id, storage = getStorage()) {
  writePendingPayments(readPendingPayments(storage).filter((r) => r.id !== id), storage);
}

export const isPendingPaymentExpired = (record, nowMs) => nowMs - Date.parse(record.started_at) > PENDING_PAYMENT_MAX_AGE_MS;

/**
 * Vuelve a consultar los cobros pendientes, uno a la vez. Borra los que terminaron (pagado o
 * cancelado) y los vencidos; deja los que siguen pendientes o no se pudieron consultar.
 *
 * @param {object} params
 * @param {(record: object, signal?: AbortSignal) => Promise<{outcome: 'paid'|'canceled'|'pending', cartId?: string | null, data?: object | null}>} params.check
 * @param {(record: object, result: {outcome: string, cartId?: string | null, error?: unknown}) => void} [params.onResult]
 * @param {AbortSignal} [params.signal]
 * @param {() => number} [params.now]
 * @returns {Promise<Array<{record: object, outcome: string}>>}
 */
export async function resolvePendingPayments({ check, onResult, signal, now = () => Date.now(), storage = getStorage() }) {
  const results = [];
  for (const record of readPendingPayments(storage)) {
    if (signal?.aborted) break;
    if (isPendingPaymentExpired(record, now())) {
      removePendingPayment(record.id, storage);
      results.push({ record, outcome: 'expired' });
      onResult?.(record, { outcome: 'expired', cartId: record.cart_id });
      continue;
    }
    let result;
    try {
      result = await check(record, signal);
    } catch (error) {
      result = { outcome: 'error', cartId: record.cart_id, error };
    }
    if (result.outcome === 'paid' || result.outcome === 'canceled') removePendingPayment(record.id, storage);
    else if (result.cartId && result.cartId !== record.cart_id) updatePendingPayment(record.id, { cart_id: result.cartId, cart_id_source: CART_ID_SOURCE.FIRESTORE }, storage);
    results.push({ record, outcome: result.outcome });
    onResult?.(record, result);
  }
  return results;
}
